import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';
import ConditionFilter from '../ConditionFilter/ConditionFilter';
import { NEW_SET, USED_SET } from '../../../util/genericHelpers';
import { FieldConfigKey } from '../../../util/enums';

// Helper: pick the first name if array
const getQueryParamName = queryParamNames =>
  Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;

// Format URI component's query param: { pub_key: 'has_all:a,b,c' } or { pub_key: 'a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options = [] } = props;

  if (name === 'productcondition') {
    return <ConditionFilter id={id} name={name} className={className} />;
  }

  const getSizeNumber = opt => {
    const raw = String(opt?.option ?? opt?.label ?? '').toLowerCase();
    const match = raw.match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
  };

  const sortedOptions =
    name === FieldConfigKey.SIZE ? [...options].sort((a, b) => getSizeNumber(a) - getSizeNumber(b)) : options;

  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {sortedOptions.map(optionConfig => {
          const { option, label, quantity } = optionConfig;
          const fieldId = `${id}.${option}`;

          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={name}
                label={label}
                value={option}
                quantity={quantity}
              />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} props.id
 * @param {string} props.name
 * @param {node} props.label
 * @param {Array<string>} props.queryParamNames
 * @param {Object} props.initialValues
 * @param {Function} props.onSubmit
 * @param {Array<Object>} props.options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType
 * @param {'has_all' | 'has_any'} props.searchMode
 * @param {boolean} [props.showAsPopup]
 * @param {number} [props.contentPlacementOffset]
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];

  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const parsedSelected = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  // For productcondition, derive the 2-option UI selection from parsedSelected
  const selectedConditionUi = React.useMemo(() => {
    if (name !== 'productcondition') return null;
    const ui = [];
    if (parsedSelected.some(o => NEW_SET.includes(o))) ui.push('new');
    if (parsedSelected.some(o => USED_SET.includes(o))) ui.push('used');
    return ui;
  }, [name, parsedSelected]);

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        {
          labelText: label,
          count:
            name === 'productcondition' ? selectedConditionUi?.length || 0 : parsedSelected.length,
        }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        {
          count:
            name === 'productcondition' ? selectedConditionUi?.length || 0 : parsedSelected.length,
        }
      )
    : '';

  // Initial values passed to FieldCheckbox inputs
  const namedInitialValues =
    name === 'productcondition'
      ? { [name]: selectedConditionUi || [] } // ['new','used'] subset
      : { [name]: parsedSelected };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;

    if (name === 'productcondition') {
      // Expand UI selections to actual enum options for the query string
      const expanded = [];
      if (Array.isArray(usedValue)) {
        if (usedValue.includes('new')) expanded.push(...NEW_SET);
        if (usedValue.includes('used')) expanded.push(...USED_SET);
      }
      onSubmit(format(expanded, queryParamName, schemaType, searchMode));
      return;
    }

    // default behavior for other filters
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
